import { useEffect, useCallback } from 'react';

/**
 * useNestedSprintEvents Hook
 * 
 * Handles nested sprint-related events and provides the trash handler:
 * - Listens for 'nestedSprintTrashed' events from sidebar deletion
 * - Listens for 'taskIconChanged' events from nested campaign icon changes
 * - Listens for 'taskNameChanged' events from nested campaign renames
 * - Listens for 'sprintIconChanged' events for optimistic icon updates
 * - Provides handleTrashNestedSprint to move nested sprints to trash
 * 
 * @param {Object} options
 * @param {Array} options.tasks - Current tasks array
 * @param {Object} options.localSprint - Current sprint object
 * @param {Function} options.setLocalSprint - Setter for local sprint state
 * @param {Object} options.actions - Sprint actions from useSprintActions
 * @param {Function} options.onTrashSprint - Parent callback to trash a sprint
 * @returns {Object} - { handleTrashNestedSprint }
 */
const useNestedSprintEvents = ({
  tasks,
  localSprint,
  setLocalSprint,
  actions,
  onTrashSprint,
}) => {
  // Handler for the X on a nested-campaign pill: trash the nested campaign but KEEP
  // the mission itself. The mission just stops being a container (nestedSprintId is
  // cleared and persisted via the normal local-sprint sync). The trashed campaign
  // leaves the sidebar (it lives in Trash and can be restored, which re-links it).
  //
  // Note: deleting the whole mission (its own delete button) is a separate path —
  // see handleConfirmDeleteTaskWithNested, which calls this and then deletes the task.
  const handleTrashNestedSprint = useCallback((taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const nestedSprintId = task?.nestedSprintId;
    
    if (!nestedSprintId) return;
    
    // Optimistically unlink locally (keeps the mission, clears the pill immediately)
    actions.handleRemoveNestedSprint(taskId);
    
    if (onTrashSprint) {
      // preserveLinkedMission: don't remove the linking mission from the parent
      onTrashSprint(nestedSprintId, { preserveLinkedMission: true });
    }
  }, [tasks, actions, onTrashSprint]);

  // Listen for nested sprint trashed events (from sidebar deletion)
  useEffect(() => {
    const handleNestedSprintTrashed = (event) => {
      const { parentSprintId, parentTaskId } = event.detail;
      
      // Only handle if this is our sprint
      if (parentSprintId === localSprint?.id) {
        actions.handleRemoveNestedSprint(parentTaskId);
      }
    };
    
    window.addEventListener('nestedSprintTrashed', handleNestedSprintTrashed);
    return () => window.removeEventListener('nestedSprintTrashed', handleNestedSprintTrashed);
  }, [localSprint?.id, actions]);

  // Listen for task icon changed events (from nested campaign icon change)
  // This ensures the parent task's icon stays in sync when the nested campaign's icon is changed
  useEffect(() => {
    const handleTaskIconChanged = (event) => {
      const { parentSprintId, taskId, newIcon } = event.detail;
      
      // Only handle if this is our sprint
      if (parentSprintId === localSprint?.id) {
        actions.handleIconChange(taskId, newIcon);
      }
    };
    
    window.addEventListener('taskIconChanged', handleTaskIconChanged);
    return () => window.removeEventListener('taskIconChanged', handleTaskIconChanged);
  }, [localSprint?.id, actions]);

  // Listen for task name changed events (from nested campaign rename).
  // Update local mission name only — do NOT use handleRenameTask, which would
  // sync the name back onto the nested campaign and risk a feedback loop.
  useEffect(() => {
    const handleTaskNameChanged = (event) => {
      const { parentSprintId, taskId, newName } = event.detail;

      if (parentSprintId !== localSprint?.id || !taskId || !newName) return;

      setLocalSprint(prev => {
        if (!prev) return prev;
        const task = prev.tasks?.find(t => t.id === taskId);
        if (!task || task.name === newName) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t =>
            t.id === taskId
              ? { ...t, name: newName, updatedAt: new Date().toISOString() }
              : t
          ),
        };
      });
    };

    window.addEventListener('taskNameChanged', handleTaskNameChanged);
    return () => window.removeEventListener('taskNameChanged', handleTaskNameChanged);
  }, [localSprint?.id, setLocalSprint]);

  // Listen for sprint icon changed events (from sidebar or other sources)
  // This enables optimistic updates in the view headers (Flow, Kanban)
  useEffect(() => {
    const handleSprintIconChanged = (event) => {
      const { sprintId, newIcon } = event.detail;
      
      // Only handle if this is our sprint
      if (sprintId === localSprint?.id) {
        setLocalSprint(prev => prev ? { ...prev, icon: newIcon } : prev);
      }
    };
    
    window.addEventListener('sprintIconChanged', handleSprintIconChanged);
    return () => window.removeEventListener('sprintIconChanged', handleSprintIconChanged);
  }, [localSprint?.id, setLocalSprint]);

  return { handleTrashNestedSprint };
};

export default useNestedSprintEvents;

